import { Component, OnInit, OnDestroy, inject, signal, computed, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { GameService } from '../../core/state/game.service';
import { CompetitionService } from '../../core/http/competition.service';
import {
  Club,
  StandingsRow,
  Matchday,
  GroupStandingItem,
  CompetitionMatchdayResponse,
  MatchLiveResponse,
  MatchLivePlayer,
  MatchLiveTeam,
  TacticalLineupResponse,
  TacticalLineupPlayer,
  SubstitutionChange,
  ConfirmSubstitutionWindowPayload,
  SubstitutionWindowResponse,
  SubstitutionWindow,
  LiveBenchPlayer,
} from '../../core/models/types';
import { FORMATION_SLOTS } from '../../core/state/squad.state';
import { FormsModule } from '@angular/forms';
import { IconComponent } from '../../shared/ui/icon/icon.component';
import { ClubShieldComponent } from '../../shared/ui/club-shield/club-shield.component';

export interface DisplayFixture {
  id: string;
  homeClubId: string;
  awayClubId: string;
  homeClubName: string;
  awayClubName: string;
  homeClubCode: string;
  awayClubCode: string;
  homeScore?: number;
  awayScore?: number;
  played: boolean;
  status: string;
  phase?: string;
  playoffType?: string;
  cycleNumber?: number;
  engineCalculation?: any;
}

@Component({
  selector: 'app-competition',
  standalone: true,
  imports: [CommonModule, FormsModule, IconComponent, ClubShieldComponent],
  templateUrl: './competition.component.html',
  styleUrl: './competition.component.scss',
  host: {
    '(document:keydown.escape)': 'onEscape()'
  }
})
export class CompetitionComponent implements OnInit, OnDestroy {
  readonly embedded = input(false);
  readonly showStandings = input(true);
  readonly showCalendar = input(true);
  readonly standingsInCard = input(false);
  // A supplied array (including an empty one) makes the parent the data owner.
  readonly suppliedStandings = input<StandingsRow[] | null>(null);
  readonly suppliedStandingsLoading = input(false);
  readonly suppliedStandingsError = input<string | null>(null);
  readonly standingsRefresh = output<void>();
  readonly effectiveStandingsLoading = computed(() => this.suppliedStandings() !== null ? this.suppliedStandingsLoading() : this.isLoadingStandings());
  readonly effectiveStandingsError = computed(() => this.suppliedStandings() !== null ? this.suppliedStandingsError() : this.standingsError());
  readonly game = inject(GameService);
  private readonly competitionService = inject(CompetitionService);
  private readonly route = inject(ActivatedRoute);

  selectedMatchdayNumber = 1;
  readonly inspectedFixture = signal<DisplayFixture | null>(null);

  // Live match details from GET /api/matches/:fixtureId/live
  readonly matchLive = signal<MatchLiveResponse | null>(null);
  readonly isLoadingLive = signal<boolean>(false);
  readonly liveError = signal<string | null>(null);
  readonly isLiveNotAvailable = signal<boolean>(false);
  readonly liveTab = signal<'both' | 'home' | 'away'>('both');
  readonly showTechnicalDetails = signal<boolean>(false);

  // Tactical substitutions and details view state
  readonly activeModalView = signal<'live' | 'substitutions' | 'details'>('live');
  readonly tacticalLineup = signal<TacticalLineupResponse | null>(null);
  readonly isLoadingTactical = signal<boolean>(false);
  readonly tacticalError = signal<string | null>(null);
  readonly isTacticalNotAvailable = signal<boolean>(false);
  readonly pendingChanges = signal<SubstitutionChange[]>([]);
  readonly selectedOutgoing = signal<string | null>(null);
  readonly selectedIncoming = signal<string | null>(null);
  readonly isSubmittingWindow = signal<boolean>(false);
  readonly lastSubmittedIdempotencyKey = signal<string | null>(null);
  readonly lastSubmittedPayload = signal<ConfirmSubstitutionWindowPayload | null>(null);
  readonly canRetryConfirmation = signal<boolean>(false);
  readonly syncingLiveMessage = signal<string | null>(null);
  readonly targetLiveRevision = signal<number | null>(null);
  private pollingIntervalId: any = null;

  // Live standings state from GET /api/groups/{competitionGroupId}/standings
  readonly liveStandings = signal<StandingsRow[]>([]);
  readonly isLoadingStandings = signal<boolean>(true);
  readonly standingsError = signal<string | null>(null);
  readonly activeCompetitionGroupId = signal<string>('');

  // Live calendar state from GET /api/competitions/seasons/{seasonId}/clubs/{clubId}/calendar
  readonly calendarResponse = signal<CompetitionMatchdayResponse | null>(null);
  readonly isLoadingCalendar = signal<boolean>(true);
  readonly calendarError = signal<string | null>(null);
  readonly activeSeasonId = signal<string>('');
  readonly activeClubId = signal<string>('');

  readonly standings = computed<StandingsRow[]>(() => {
    const supplied = this.suppliedStandings();
    if (supplied !== null) return supplied;
    const live = this.liveStandings();
    if (live.length > 0) return live;
    if (!this.isLoadingStandings() && this.standingsError()) {
      return this.game.standings();
    }
    return [];
  });

  readonly currentMatchdayNumber = computed<number | null>(() => {
    return this.calendarResponse()?.matchdayNumber ?? null;
  });

  readonly totalMatchdays = computed<number | null>(() => {
    return this.calendarResponse()?.totalMatchdays ?? null;
  });

  readonly restingClubs = computed(() => {
    const calendar = this.calendarResponse();
    if (!calendar) return [];
    if (calendar.restingClubs !== undefined) return calendar.restingClubs;
    // Older responses identify only the requesting club's rest day.
    return calendar.clubResting && calendar.club ? [calendar.club] : [];
  });

  readonly fixtures = computed<DisplayFixture[]>(() => {
    const cal = this.calendarResponse();
    if (cal && cal.fixtures && cal.fixtures.length > 0) {
      return cal.fixtures.map((f) => {
        const home = this.getClub(f.homeClub.id);
        const away = this.getClub(f.awayClub.id);
        const played =
          f.status === 'FINISHED' ||
          (f.homeGoals !== null && f.homeGoals !== undefined);

        return {
          id: f.id,
          homeClubId: f.homeClub.id,
          awayClubId: f.awayClub.id,
          homeClubName: f.homeClub.name || home?.name || 'Club Local',
          awayClubName: f.awayClub.name || away?.name || 'Club Visitante',
          homeClubCode: this.getBadgeCode(f.homeClub),
          awayClubCode: this.getBadgeCode(f.awayClub),
          homeScore: f.homeGoals !== null && f.homeGoals !== undefined ? f.homeGoals : undefined,
          awayScore: f.awayGoals !== null && f.awayGoals !== undefined ? f.awayGoals : undefined,
          played,
          status: f.status,
        };
      });
    }

    // Only fallback if the calendar request has concluded with an error and no calendar was loaded
    if (!this.isLoadingCalendar() && this.calendarError()) {
      const md = this.game.matchdays().find((m) => m.number === this.selectedMatchdayNumber);
      if (!md) return [];
      return md.fixtures.map((f) => {
        const home = this.getClub(f.homeClubId);
        const away = this.getClub(f.awayClubId);
        return {
          id: f.id,
          homeClubId: f.homeClubId,
          awayClubId: f.awayClubId,
          homeClubName: home?.name || 'Club Local',
          awayClubName: away?.name || 'Club Visitante',
          homeClubCode: home?.code || 'LOC',
          awayClubCode: away?.code || 'VIS',
          homeScore: f.homeScore,
          awayScore: f.awayScore,
          played: f.played,
          status: f.played ? 'FINISHED' : 'PLANNED',
          engineCalculation: f.engineCalculation,
        };
      });
    }

    return [];
  });

  async ngOnInit(): Promise<void> {
    this.selectedMatchdayNumber = this.game.currentMatchdayNumber();

    if (this.suppliedStandings() !== null && !this.showCalendar()) return;

    // Standings belong to the selected club. Legacy group query parameters cannot change context.
    let groupId: string | null = null;
    try { groupId = (await this.competitionService.resolveCurrentCompetitionContext()).clubId || null; }
    catch { groupId = null; }

    if (!groupId || groupId === 'league-global-01') {
      this.standingsError.set('No se encontró un grupo de competición activo para el usuario.');
      this.calendarError.set('No se encontró un grupo de competición activo para el usuario.');
      this.isLoadingStandings.set(false);
      this.isLoadingCalendar.set(false);
      return;
    }

    this.activeCompetitionGroupId.set(groupId);

    // 2. Load standings & calendar in parallel
    await Promise.all([
      this.showStandings() && this.suppliedStandings() === null ? this.loadStandings(groupId) : Promise.resolve(),
      this.showCalendar() ? this.loadCalendar() : Promise.resolve(),
    ]);
  }

  async resolveSeasonAndClubIds(): Promise<{ seasonId: string; clubId: string }> {
    return this.competitionService.resolveActiveSeasonAndClubId();
  }

  async loadCalendar(matchdayNumber?: number, forceRefresh = false): Promise<void> {
    this.isLoadingCalendar.set(true);
    this.calendarError.set(null);

    try {
      const { seasonId, clubId } = await this.resolveSeasonAndClubIds();
      this.activeSeasonId.set(seasonId);
      this.activeClubId.set(clubId);

      // GET /api/competitions/seasons/{seasonId}/clubs/{clubId}/calendar(?matchdayNumber=X)
      const resp = await this.competitionService.getClubCalendar(
        seasonId,
        clubId,
        matchdayNumber,
        forceRefresh
      );

      if (resp) {
        this.calendarResponse.set(resp);
        this.selectedMatchdayNumber = resp.matchdayNumber;
      }
    } catch (err: any) {
      const msg = err?.message || 'Error al obtener el calendario de la competición.';
      this.calendarError.set(msg);
    } finally {
      this.isLoadingCalendar.set(false);
    }
  }

  async refreshCalendar(): Promise<void> {
    const num = this.currentMatchdayNumber();
    await this.loadCalendar(num !== null ? num : undefined, true);
  }

  async loadStandings(groupId: string, forceRefresh = false): Promise<void> {
    this.isLoadingStandings.set(true);
    this.standingsError.set(null);

    try {
      const items: GroupStandingItem[] = await this.competitionService.getGroupStandings(groupId, forceRefresh);

      if (Array.isArray(items) && items.length > 0) {
        const rows: StandingsRow[] = items.map((item, idx) => {
          const club =
            this.getClub(item.clubId) ||
            this.game.clubs().find(
              (c) =>
                c.id === item.clubId ||
                c.name.toLowerCase() === item.clubName.toLowerCase()
            );

          const rawCode = item.clubCode || (item as any).code || club?.code;
          const clubCode =
            rawCode && rawCode.length <= 4 && !rawCode.includes('-')
              ? rawCode.toUpperCase()
              : this.getBadgeCode({ id: item.clubId, name: item.clubName, code: rawCode });

          return {
            clubId: item.clubId,
            shield: item.shield,
            clubName: item.clubName,
            clubCode,
            played: item.played ?? 0,
            sportingMatchesPlayed: item.sportingMatchesPlayed, administrativeLosses: item.administrativeLosses, byes: item.byes,
            won: item.won ?? 0,
            drawn: item.drawn ?? 0,
            lost: item.lost ?? 0,
            goalsFor: item.goalsFor ?? 0,
            goalsAgainst: item.goalsAgainst ?? 0,
            goalDifference:
              item.goalDifference !== undefined
                ? item.goalDifference
                : (item.goalsFor ?? 0) - (item.goalsAgainst ?? 0),
            points: item.points ?? 0,
            recentForm: item.recentForm || [],
            position: item.position ?? idx + 1,
          };
        });

        // Preserve the authoritative order and shared positions from the server.

        this.liveStandings.set(rows);
        this.game.setStandings(rows);
      }
    } catch (err: any) {
      const msg = err?.message || 'Error al obtener la clasificación en vivo.';
      this.standingsError.set(msg);
    } finally {
      this.isLoadingStandings.set(false);
    }
  }

  async refreshStandings(): Promise<void> {
    if (this.suppliedStandings() !== null) {
      this.standingsRefresh.emit();
      return;
    }
    const gid = this.activeCompetitionGroupId();
    if (gid) {
      await this.loadStandings(gid, true);
    }
  }

  isUserClub(row: StandingsRow): boolean {
    const userClub = this.game.userClub();
    const activeClubId = this.activeClubId() || this.game.activeUserClubId();
    if (activeClubId && activeClubId === row.clubId) {
      return true;
    }
    if (
      userClub &&
      (userClub.id === row.clubId ||
        userClub.name.toLowerCase() === row.clubName.toLowerCase())
    ) {
      return true;
    }
    return false;
  }

  isUserFixture(fixture: DisplayFixture): boolean {
    const activeClub = this.activeClubId() || this.game.activeUserClubId();
    const userClub = this.game.userClub();
    if (activeClub && (fixture.homeClubId === activeClub || fixture.awayClubId === activeClub)) {
      return true;
    }
    if (userClub && (fixture.homeClubId === userClub.id || fixture.awayClubId === userClub.id)) {
      return true;
    }
    return false;
  }

  getBadgeCode(club: { id?: string; name?: string; code?: string }): string {
    const rawCode = club.code;
    if (rawCode && rawCode.length <= 4 && !rawCode.includes('-')) {
      return rawCode.toUpperCase();
    }
    const name = club.name || '';
    if (name) {
      const clean = name.replace(/[^A-Za-z0-9]/g, '');
      if (clean.length > 0) {
        return clean.slice(0, 3).toUpperCase();
      }
    }
    return 'CLB';
  }

  openClubProfile(clubId: string, name?: string, code?: string): void {
    this.game.openClubProfile(clubId, name, code);
  }

  getClubColor(row: StandingsRow): string {
    const club = this.getClub(row.clubId);
    if (club?.primaryColor) return club.primaryColor;
    return this.getColorForString(row.clubName || row.clubId);
  }

  getClubBadgeColor(clubId: string, name?: string): string {
    const club = this.getClub(clubId);
    if (club?.primaryColor) return club.primaryColor;
    return this.getColorForString(name || clubId);
  }

  private getColorForString(str: string): string {
    const PALETTE = [
      '#2563eb', '#dc2626', '#059669', '#d97706', '#7c3aed',
      '#0891b2', '#4f46e5', '#db2777', '#ea580c', '#0284c7'
    ];
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return PALETTE[Math.abs(hash) % PALETTE.length];
  }

  currentMatchday(): Matchday | undefined {
    return this.game.matchdays().find((m) => m.number === this.selectedMatchdayNumber);
  }

  getClub(id: string): Club | undefined {
    return this.game.clubs().find((c) => c.id === id);
  }

  canGoPrev(): boolean {
    if (this.isLoadingCalendar()) return false;
    const cal = this.calendarResponse();
    if (cal) {
      // null is the server's explicit start-of-calendar boundary.
      if (cal.previousMatchdayNumber !== undefined) {
        if (cal.previousMatchdayNumber === null) return false;
        return cal.previousMatchdayNumber >= 1;
      }
      return cal.matchdayNumber > 1;
    }
    return false;
  }

  canGoNext(): boolean {
    if (this.isLoadingCalendar()) return false;
    const cal = this.calendarResponse();
    if (cal) {
      if (cal.nextMatchdayNumber !== null && cal.nextMatchdayNumber !== undefined) {
        return cal.nextMatchdayNumber <= (cal.totalMatchdays || 38);
      }
      return cal.matchdayNumber < (cal.totalMatchdays || 38);
    }
    return false;
  }

  async prevMatchday(): Promise<void> {
    if (!this.canGoPrev() || this.isLoadingCalendar()) return;
    const cal = this.calendarResponse();
    if (cal) {
      const prev = cal.previousMatchdayNumber === undefined ? cal.matchdayNumber - 1 : cal.previousMatchdayNumber;
      if (prev !== null && prev >= 1) {
        await this.loadCalendar(prev);
      }
    } else {
      this.selectedMatchdayNumber--;
    }
  }

  async nextMatchdayNav(): Promise<void> {
    if (!this.canGoNext() || this.isLoadingCalendar()) return;
    const cal = this.calendarResponse();
    if (cal) {
      const next = cal.nextMatchdayNumber ?? (cal.matchdayNumber + 1);
      const total = cal.totalMatchdays || 38;
      if (next <= total) {
        await this.loadCalendar(next);
      }
    } else {
      this.selectedMatchdayNumber++;
    }
  }

  matchdayStatusText(): string {
    const cal = this.calendarResponse();
    if (cal) {
      if (cal.status === 'FINISHED' || cal.windowStatus === 'CLOSED') {
        return 'Finalizada';
      }
      if (cal.status === 'IN_PROGRESS' || cal.windowStatus === 'ACTIVE') {
        return 'En juego';
      }
      if (cal.windowStatus === 'UPCOMING' || cal.status === 'PLANNED') {
        if (cal.startsAt) {
          try {
            const d = new Date(cal.startsAt);
            return `Próxima (${d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })})`;
          } catch {
            return 'Próxima jornada';
          }
        }
        return 'Próxima jornada';
      }
      return cal.status || 'Programada';
    }
    return this.currentMatchday()?.status === 'CLOSED' ? 'Finalizada' : 'Pendiente de disputa';
  }

  ngOnDestroy(): void {
    this.stopLivePolling();
  }

  isUserClubFixture(): boolean {
    const userClubId = this.getUserClubId();
    const f = this.inspectedFixture();
    return !!(f && userClubId && (f.homeClubId === userClubId || f.awayClubId === userClubId));
  }

  getUserClubId(): string {
    return this.game.activeUserClubId() || this.game.userClub()?.id || '';
  }

  getMatchdayId(): string {
    return (
      this.matchLive()?.matchdayId ||
      this.calendarResponse()?.matchdayId ||
      this.currentMatchday()?.id ||
      String(this.selectedMatchdayNumber)
    );
  }

  readonly tacticalPitchSlots = computed(() => {
    const tl = this.tacticalLineup();
    if (!tl?.lineup) return [];
    const formation = tl.lineup.formation || '4-4-2';
    const formationSlots = FORMATION_SLOTS[formation] || FORMATION_SLOTS['4-4-2'] || [];
    const starters = tl.lineup.starters || [];

    return formationSlots.map((slotConfig, idx) => {
      const slotNumber = idx + 1;
      const player =
        starters.find(
          (s) => Number(s.slot) === slotNumber || s.slot === slotNumber
        ) ||
        starters[idx] ||
        null;

      return {
        slotNumber,
        id: slotConfig.id,
        label: slotConfig.label,
        pos: slotConfig.pos,
        x: slotConfig.x,
        y: slotConfig.y,
        player,
      };
    });
  });

  readonly availableStartersForSub = computed(() => {
    const tl = this.tacticalLineup();
    if (!tl?.lineup?.starters) return [];
    const usedOutgoings = new Set(this.pendingChanges().map((c) => c.outgoing));
    return tl.lineup.starters.filter((p) => !usedOutgoings.has(p.playerInstanceId));
  });

  readonly availableBenchForSub = computed(() => {
    const tl = this.tacticalLineup();
    if (!tl?.lineup?.bench) return [];
    const retiredSet = new Set(tl.retired || []);
    const usedIncomings = new Set(this.pendingChanges().map((c) => c.incoming));
    return tl.lineup.bench.filter(
      (p) => !p.retired && !retiredSet.has(p.playerInstanceId) && !usedIncomings.has(p.playerInstanceId)
    );
  });

  isStarterSelectedAsOutgoing(playerInstanceId: string | undefined): boolean {
    return !!playerInstanceId && this.selectedOutgoing() === playerInstanceId;
  }

  isBenchSelectedAsIncoming(playerInstanceId: string | undefined): boolean {
    return !!playerInstanceId && this.selectedIncoming() === playerInstanceId;
  }

  getPendingChangeForStarter(playerInstanceId: string | undefined): { change: SubstitutionChange; index: number; incomingName: string } | null {
    if (!playerInstanceId) return null;
    const idx = this.pendingChanges().findIndex((c) => c.outgoing === playerInstanceId);
    if (idx === -1) return null;
    const change = this.pendingChanges()[idx];
    return {
      change,
      index: idx,
      incomingName: this.getBenchDisplayName(change.incoming),
    };
  }

  getPendingChangeForBench(playerInstanceId: string | undefined): { change: SubstitutionChange; index: number; outgoingName: string } | null {
    if (!playerInstanceId) return null;
    const idx = this.pendingChanges().findIndex((c) => c.incoming === playerInstanceId);
    if (idx === -1) return null;
    const change = this.pendingChanges()[idx];
    return {
      change,
      index: idx,
      outgoingName: this.getStarterDisplayName(change.outgoing),
    };
  }

  isStarterInPendingChanges(playerInstanceId: string | undefined): boolean {
    if (!playerInstanceId) return false;
    return this.pendingChanges().some((c) => c.outgoing === playerInstanceId);
  }

  isBenchInPendingChanges(playerInstanceId: string | undefined): boolean {
    if (!playerInstanceId) return false;
    return this.pendingChanges().some((c) => c.incoming === playerInstanceId);
  }

  isBenchPlayerRetired(playerInstanceId: string | undefined): boolean {
    if (!playerInstanceId) return false;
    const tl = this.tacticalLineup();
    if (!tl) return false;
    if (tl.retired && tl.retired.includes(playerInstanceId)) return true;
    const benchP = tl.lineup?.bench?.find((b) => b.playerInstanceId === playerInstanceId);
    return !!benchP?.retired;
  }

  handleStarterPitchClick(player: TacticalLineupPlayer | null): void {
    if (!player) return;
    const pId = player.playerInstanceId;
    const pending = this.getPendingChangeForStarter(pId);
    if (pending) {
      this.removeExchangeFromBatch(pending.index);
      this.game.ui.showNotification(`Se ha retirado del lote el cambio de ${player.displayName || 'jugador'}`, 'info');
      return;
    }

    if (this.selectedOutgoing() === pId) {
      this.selectedOutgoing.set(null);
      return;
    }

    this.selectedOutgoing.set(pId);
  }

  handleBenchPlayerClick(player: TacticalLineupPlayer): void {
    const pId = player.playerInstanceId;
    if (this.isBenchPlayerRetired(pId)) {
      this.game.ui.showNotification('Este jugador está retirado y no puede volver a participar', 'warning');
      return;
    }

    const pending = this.getPendingChangeForBench(pId);
    if (pending) {
      this.removeExchangeFromBatch(pending.index);
      this.game.ui.showNotification(`Se ha retirado del lote el cambio de ${player.displayName || 'suplente'}`, 'info');
      return;
    }

    if (this.selectedIncoming() === pId) {
      this.selectedIncoming.set(null);
      return;
    }

    this.selectedIncoming.set(pId);
  }

  clearActiveSelection(): void {
    this.selectedOutgoing.set(null);
    this.selectedIncoming.set(null);
  }

  getSelectedOutgoingPlayer(): TacticalLineupPlayer | null {
    const outId = this.selectedOutgoing();
    if (!outId) return null;
    return this.tacticalLineup()?.lineup?.starters?.find((s) => s.playerInstanceId === outId) || null;
  }

  getSelectedIncomingPlayer(): TacticalLineupPlayer | null {
    const inId = this.selectedIncoming();
    if (!inId) return null;
    return this.tacticalLineup()?.lineup?.bench?.find((b) => b.playerInstanceId === inId) || null;
  }

  getPositionBadgeClass(pos?: string): string {
    if (!pos) return 'bg-slate-800 text-slate-300 border-slate-700';
    const upper = pos.toUpperCase();
    if (upper.includes('GOAL') || upper === 'POR') {
      return 'bg-amber-950/90 text-amber-300 border-amber-600/60';
    }
    if (upper.includes('DEF') || upper === 'DF') {
      return 'bg-blue-950/90 text-blue-300 border-blue-600/60';
    }
    if (upper.includes('MID') || upper === 'MED' || upper === 'MC') {
      return 'bg-emerald-950/90 text-emerald-300 border-emerald-600/60';
    }
    return 'bg-rose-950/90 text-rose-300 border-rose-600/60';
  }

  getPlayerShortLastName(name?: string): string {
    if (!name) return 'Jugador';
    const parts = name.trim().split(' ');
    return parts[parts.length - 1];
  }

  getStarterDisplayName(playerInstanceId: string): string {
    const p = this.tacticalLineup()?.lineup?.starters?.find((s) => s.playerInstanceId === playerInstanceId);
    return p?.displayName || playerInstanceId.substring(0, 8);
  }

  getBenchDisplayName(playerInstanceId: string): string {
    const p = this.tacticalLineup()?.lineup?.bench?.find((s) => s.playerInstanceId === playerInstanceId);
    return p?.displayName || playerInstanceId.substring(0, 8);
  }

  addExchangeToBatch(): void {
    const outgoing = this.selectedOutgoing();
    const incoming = this.selectedIncoming();
    if (!outgoing || !incoming) return;

    const remainingSubs = this.tacticalLineup()?.substitutionsRemaining ?? 0;
    if (this.pendingChanges().length >= remainingSubs) {
      this.game.ui.showNotification(`No puedes superar las ${remainingSubs} sustituciones restantes`, 'warning');
      return;
    }

    const batch = this.pendingChanges();
    const isOutUsed = batch.some((c) => c.outgoing === outgoing || c.incoming === outgoing);
    const isInUsed = batch.some((c) => c.outgoing === incoming || c.incoming === incoming);
    if (isOutUsed || isInUsed || outgoing === incoming) {
      this.game.ui.showNotification('No se pueden repetir participantes dentro del mismo lote', 'warning');
      return;
    }

    this.pendingChanges.update((prev) => [...prev, { outgoing, incoming }]);
    this.selectedOutgoing.set(null);
    this.selectedIncoming.set(null);
  }

  removeExchangeFromBatch(index: number): void {
    this.pendingChanges.update((prev) => prev.filter((_, idx) => idx !== index));
  }

  async loadTacticalLineup(): Promise<void> {
    const clubId = this.getUserClubId();
    const matchdayId = this.getMatchdayId();
    if (!clubId || !matchdayId) return;

    this.isLoadingTactical.set(true);
    this.tacticalError.set(null);
    this.isTacticalNotAvailable.set(false);

    try {
      const res = await this.competitionService.getTacticalLineup(clubId, matchdayId);
      this.tacticalLineup.set(res);
      this.isTacticalNotAvailable.set(!res);
    } catch (err: any) {
      const status = err?.status || err?.statusCode;
      if (status === 404) {
        this.tacticalLineup.set(null);
        this.isTacticalNotAvailable.set(true);
        this.tacticalError.set('No hay captura disponible; un descanso no admite sustituciones.');
      } else {
        this.tacticalError.set(err?.message || 'No se pudo cargar la alineación táctica.');
      }
    } finally {
      this.isLoadingTactical.set(false);
    }
  }

  async confirmSubstitutions(isRetry = false): Promise<void> {
    if (this.isSubmittingWindow()) return;

    const clubId = this.getUserClubId();
    const matchdayId = this.getMatchdayId();
    if (!clubId || !matchdayId) return;

    let idempotencyKey: string;
    let payload: ConfirmSubstitutionWindowPayload;

    if (isRetry) {
      if (!this.lastSubmittedIdempotencyKey() || !this.lastSubmittedPayload()) return;
      idempotencyKey = this.lastSubmittedIdempotencyKey()!;
      payload = this.lastSubmittedPayload()!;
    } else {
      if (this.pendingChanges().length === 0) {
        this.game.ui.showNotification('Añade al menos un intercambio al lote', 'warning');
        return;
      }
      const tl = this.tacticalLineup();
      if (!tl) return;
      if (tl.windowsRemaining <= 0) {
        this.game.ui.showNotification('No quedan ventanas de sustitución disponibles', 'error');
        return;
      }
      if (this.pendingChanges().length > tl.substitutionsRemaining) {
        this.game.ui.showNotification('El lote supera las sustituciones restantes', 'error');
        return;
      }

      idempotencyKey =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `sub-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      payload = {
        expectedRevision: tl.lineup.revision,
        changes: [...this.pendingChanges()],
      };
      this.lastSubmittedIdempotencyKey.set(idempotencyKey);
      this.lastSubmittedPayload.set(payload);
    }

    this.isSubmittingWindow.set(true);

    try {
      const res = await this.competitionService.confirmSubstitutionWindow(
        clubId,
        matchdayId,
        payload,
        idempotencyKey
      );

      this.canRetryConfirmation.set(false);
      this.pendingChanges.set([]);
      this.game.ui.showNotification('¡Ventana de sustituciones confirmada!', 'success');

      // Releer tactical-lineup y live
      this.targetLiveRevision.set(res.revisionAfter);
      this.syncingLiveMessage.set('Cambios guardados; actualizando Live');

      await Promise.all([
        this.loadTacticalLineup(),
        this.loadMatchLive(this.inspectedFixture()!.id),
      ]);

      this.startLivePolling(res.revisionAfter);
    } catch (err: any) {
      const status = err?.status || err?.statusCode;
      if (status === 400) {
        this.game.ui.showNotification('Petición inválida. No se han guardado los cambios.', 'error');
        this.canRetryConfirmation.set(false);
      } else if (status === 409) {
        this.game.ui.showNotification(
          err?.message || 'Conflicto de versión o jornada cerrada. Actualizando estado...',
          'warning'
        );
        this.canRetryConfirmation.set(false);
        await this.loadTacticalLineup();
      } else if (status === 422) {
        this.game.ui.showNotification(
          err?.message || 'Ventana rechazada por reglas, participantes o plazo.',
          'error'
        );
        this.canRetryConfirmation.set(false);
        await this.loadTacticalLineup();
      } else if (status === 503 || !status || status >= 500) {
        this.canRetryConfirmation.set(true);
        this.game.ui.showNotification(
          'Indisponibilidad del servidor o conexión incierta. Puedes reintentar la confirmación.',
          'error'
        );
      } else {
        this.game.ui.showNotification(err?.message || 'Error al confirmar sustituciones', 'error');
        this.canRetryConfirmation.set(false);
      }
    } finally {
      this.isSubmittingWindow.set(false);
    }
  }

  private startLivePolling(revisionAfter: number): void {
    this.stopLivePolling();
    this.pollingIntervalId = setInterval(async () => {
      const fixture = this.inspectedFixture();
      if (!fixture) {
        this.stopLivePolling();
        return;
      }
      await this.loadMatchLive(fixture.id);
      const live = this.matchLive();
      const userClubId = this.getUserClubId();
      const isHome = fixture.homeClubId === userClubId;
      const currentRev = isHome ? live?.home?.lineupRevision : live?.away?.lineupRevision;

      if (currentRev !== undefined && currentRev >= revisionAfter) {
        this.syncingLiveMessage.set(null);
        this.targetLiveRevision.set(null);
        this.stopLivePolling();
      }
    }, 3000);
  }

  stopLivePolling(): void {
    if (this.pollingIntervalId) {
      clearInterval(this.pollingIntervalId);
      this.pollingIntervalId = null;
    }
  }

  async inspectFixture(fixture: DisplayFixture): Promise<void> {
    this.inspectedFixture.set(fixture);
    this.liveTab.set('both');
    this.showTechnicalDetails.set(false);
    this.activeModalView.set('live');
    this.pendingChanges.set([]);
    this.selectedOutgoing.set(null);
    this.selectedIncoming.set(null);
    this.canRetryConfirmation.set(false);
    this.syncingLiveMessage.set(null);
    this.targetLiveRevision.set(null);
    this.stopLivePolling();

    await this.loadMatchLive(fixture.id);

    if (this.isUserClubFixture()) {
      void this.loadTacticalLineup();
    }
  }

  async loadMatchLive(fixtureId: string): Promise<void> {
    this.isLoadingLive.set(true);
    this.liveError.set(null);
    this.isLiveNotAvailable.set(false);
    try {
      const res = await this.competitionService.getMatchLive(fixtureId);
      this.matchLive.set(res);
      this.isLiveNotAvailable.set(!res);
    } catch (err: any) {
      const status = err?.status || err?.statusCode;
      const is404 =
        status === 404 ||
        err?.message?.includes('404') ||
        (typeof err?.message === 'string' && err.message.toLowerCase().includes('not found'));

      if (is404) {
        this.matchLive.set(null);
        this.isLiveNotAvailable.set(true);
        this.liveError.set(null);
      } else {
        this.isLiveNotAvailable.set(false);
        const msg = err?.message || 'No se pudieron cargar los datos en directo del partido.';
        this.liveError.set(msg);
      }
    } finally {
      this.isLoadingLive.set(false);
    }
  }

  closeInspectModal(): void {
    this.stopLivePolling();
    this.inspectedFixture.set(null);
    this.matchLive.set(null);
    this.tacticalLineup.set(null);
    this.liveError.set(null);
    this.isLiveNotAvailable.set(false);
    this.showTechnicalDetails.set(false);
    this.activeModalView.set('live');
    this.pendingChanges.set([]);
    this.selectedOutgoing.set(null);
    this.selectedIncoming.set(null);
    this.canRetryConfirmation.set(false);
    this.syncingLiveMessage.set(null);
    this.targetLiveRevision.set(null);
  }

  toggleTechnicalDetails(): void {
    if (this.activeModalView() === 'details') {
      this.activeModalView.set('live');
    } else {
      this.activeModalView.set('details');
    }
  }

  formatNumber(val?: number | null, decimals: number = 4): string {
    if (val === null || val === undefined || isNaN(val)) return '—';
    return Number(val).toFixed(decimals);
  }

  formatPercent(val?: number | null): string {
    if (val === null || val === undefined || isNaN(val)) return '—';
    const num = Number(val);
    const prefix = num > 0 ? '+' : '';
    return `${prefix}${num.toFixed(2)}%`;
  }

  getPlayerDisplayName(p: MatchLivePlayer): string {
    if (p.displayName && p.displayName.trim().length > 0) {
      return p.displayName.trim();
    }
    const all = this.game.allPlayers();
    const found = all.find(
      (item) =>
        item.id === p.playerInstanceId ||
        item.playerInstanceId === p.playerInstanceId ||
        (p.playerDefinitionId && item.playerDefinitionId === p.playerDefinitionId)
    );
    if (found?.name) return found.name;
    if (found?.shortName) return found.shortName;
    if (p.type === 'AMATEUR') {
      const posMap: Record<string, string> = {
        GOALKEEPER: 'Portero Amateur',
        DEFENCE: 'Defensa Amateur',
        MIDFIELD: 'Medio Amateur',
        FORWARD: 'Delantero Amateur',
      };
      return posMap[p.line] || 'Amateur';
    }
    if (p.playerDefinitionId) {
      return `Jugador #${p.playerDefinitionId.slice(0, 6)}`;
    }
    return `Jugador #${p.playerInstanceId.slice(0, 6)}`;
  }

  getPlayerRealClub(p: MatchLivePlayer | LiveBenchPlayer | any): { id?: string; name?: string; shortName?: string } | null {
    if (!p) return null;
    if (p.realClub?.shortName || p.realClub?.name) {
      return p.realClub;
    }
    const all = this.game.allPlayers();
    const found = all.find(
      (item) =>
        item.id === p.playerInstanceId ||
        item.playerInstanceId === p.playerInstanceId ||
        (p.playerDefinitionId && item.playerDefinitionId === p.playerDefinitionId)
    );
    return found?.realClub || null;
  }

  getPlayerRealClubCrestUrl(p: MatchLivePlayer | LiveBenchPlayer | any): string | null {
    const rc = this.getPlayerRealClub(p);
    const code = rc?.shortName?.trim().toUpperCase();
    if (!code) return null;
    return `assets/crest/${code}.png`;
  }

  getPlayerRealClubName(p: MatchLivePlayer | LiveBenchPlayer | any): string {
    const rc = this.getPlayerRealClub(p);
    return rc?.name || rc?.shortName || 'Club Real';
  }

  onCrestError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img) {
      img.style.display = 'none';
    }
  }

  getPlayerLineBadge(line: string): { code: string; label: string; class: string } {
    switch (line?.toUpperCase()) {
      case 'GOALKEEPER':
        return {
          code: 'POR',
          label: 'Portería',
          class: 'bg-amber-100 text-amber-800 dark:bg-amber-950/70 dark:text-amber-300 border-amber-300 dark:border-amber-800',
        };
      case 'DEFENCE':
        return {
          code: 'DEF',
          label: 'Defensa',
          class: 'bg-blue-100 text-blue-800 dark:bg-blue-950/70 dark:text-blue-300 border-blue-300 dark:border-blue-800',
        };
      case 'MIDFIELD':
        return {
          code: 'MED',
          label: 'Control',
          class: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/70 dark:text-emerald-300 border-emerald-300 dark:border-emerald-800',
        };
      case 'FORWARD':
        return {
          code: 'DEL',
          label: 'Ataque',
          class: 'bg-rose-100 text-rose-800 dark:bg-rose-950/70 dark:text-rose-300 border-rose-300 dark:border-rose-800',
        };
      default:
        return {
          code: 'JUG',
          label: line || 'Línea',
          class: 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300 border-slate-300 dark:border-slate-700',
        };
    }
  }

  getGroupedPlayers(players: MatchLivePlayer[]): { line: string; label: string; players: MatchLivePlayer[] }[] {
    if (!players || players.length === 0) return [];
    const linesOrder = ['GOALKEEPER', 'DEFENCE', 'MIDFIELD', 'FORWARD'];
    const labels: Record<string, string> = {
      GOALKEEPER: 'Portería',
      DEFENCE: 'Defensas',
      MIDFIELD: 'Centrocampistas',
      FORWARD: 'Delanteros',
    };

    return linesOrder
      .map((line) => ({
        line,
        label: labels[line] || line,
        players: players.filter((p) => p.line === line),
      }))
      .filter((group) => group.players.length > 0);
  }

  getTeamTotalPoints(players?: MatchLivePlayer[]): number | null {
    if (!players || players.length === 0) return null;
    const scoredPlayers = players.filter((p) => p.points !== null && p.points !== undefined);
    if (scoredPlayers.length === 0) return null;
    const sum = scoredPlayers.reduce((acc, p) => acc + (p.points || 0), 0);
    return Math.round(sum * 100) / 100;
  }

  getLiveStatusBadge(status?: string): { label: string; class: string; icon: string } {
    const s = status || this.matchLive()?.status || this.inspectedFixture()?.status || 'PLANNED';
    switch (s) {
      case 'WAITING_FOR_POINTS':
        return {
          label: 'Esperando puntuaciones',
          class: 'bg-amber-100 text-amber-800 dark:bg-amber-950/70 dark:text-amber-300 border-amber-300 dark:border-amber-800',
          icon: 'clock',
        };
      case 'IN_PROGRESS':
        return {
          label: 'En juego',
          class: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/70 dark:text-emerald-300 border-emerald-300 dark:border-emerald-800 animate-pulse',
          icon: 'activity',
        };
      case 'FINISHED':
        return {
          label: 'Finalizado',
          class: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-slate-300 dark:border-slate-700',
          icon: 'check-circle',
        };
      case 'PLANNED':
      case 'SCHEDULED':
      case 'PROGRAMADO':
        return {
          label: 'Programado',
          class: 'bg-blue-100 text-blue-800 dark:bg-blue-950/70 dark:text-blue-300 border-blue-300 dark:border-blue-800',
          icon: 'calendar',
        };
      default:
        return {
          label: s,
          class: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-slate-300 dark:border-slate-700',
          icon: 'info',
        };
    }
  }

  getLiveScoreDisplay(): { home: string | number; away: string | number; isFinal: boolean } {
    const live = this.matchLive();
    const fix = this.inspectedFixture();

    let home: string | number = '-';
    let away: string | number = '-';
    let isFinal = false;

    if (live?.homeGoals !== null && live?.homeGoals !== undefined) {
      home = live.homeGoals;
    } else if (live?.home?.staffRatings && live.status === 'WAITING_FOR_POINTS') {
      home = '...';
    } else if (fix?.homeScore !== undefined) {
      home = fix.homeScore;
    }

    if (live?.awayGoals !== null && live?.awayGoals !== undefined) {
      away = live.awayGoals;
    } else if (live?.away?.staffRatings && live.status === 'WAITING_FOR_POINTS') {
      away = '...';
    } else if (fix?.awayScore !== undefined) {
      away = fix.awayScore;
    }

    if (live?.status === 'FINISHED' || fix?.status === 'FINISHED' || fix?.played) {
      isFinal = true;
    }

    return { home, away, isFinal };
  }

  getScoredPlayersCount(players?: MatchLivePlayer[]): { scored: number; total: number } {
    if (!players || !Array.isArray(players)) {
      return { scored: 0, total: 11 };
    }
    const scored = players.filter(p => p.points !== null && p.points !== undefined).length;
    const total = players.length > 0 ? players.length : 11;
    return { scored, total };
  }

  getMatchTrend(): {
    homePower: number;
    awayPower: number;
    leader: 'home' | 'away' | 'tie';
    leadPower: number;
    homePoints: number;
    awayPoints: number;
    homeScoredPlayers: number;
    homeTotalPlayers: number;
    awayScoredPlayers: number;
    awayTotalPlayers: number;
  } {
    const live = this.matchLive();
    if (!live) {
      return {
        homePower: 50,
        awayPower: 50,
        leader: 'tie',
        leadPower: 50,
        homePoints: 0,
        awayPoints: 0,
        homeScoredPlayers: 0,
        homeTotalPlayers: 11,
        awayScoredPlayers: 0,
        awayTotalPlayers: 11,
      };
    }

    // Compare accumulated player points without tactical or staff weighting.
    const extractPoints = (team?: MatchLiveTeam): number =>
      (team?.players ?? []).reduce((total, player) =>
        total + (typeof player.points === 'number' && Number.isFinite(player.points) ? player.points : 0), 0);

    const homePoints = extractPoints(live.home);
    const awayPoints = extractPoints(live.away);
    const homeWeight = Math.max(0, homePoints);
    const awayWeight = Math.max(0, awayPoints);
    const total = homeWeight + awayWeight;

    let homePower = 50;
    let awayPower = 50;

    if (total > 0) {
      homePower = Math.round((homeWeight / total) * 100);
      awayPower = 100 - homePower;
    } else {
      homePower = 50;
      awayPower = 50;
    }

    const leader = homePower > awayPower ? 'home' : awayPower > homePower ? 'away' : 'tie';
    const leadPower = Math.max(homePower, awayPower);

    const homeCount = this.getScoredPlayersCount(live.home?.players);
    const awayCount = this.getScoredPlayersCount(live.away?.players);

    return {
      homePower,
      awayPower,
      leader,
      leadPower,
      homePoints,
      awayPoints,
      homeScoredPlayers: homeCount.scored,
      homeTotalPlayers: homeCount.total,
      awayScoredPlayers: awayCount.scored,
      awayTotalPlayers: awayCount.total,
    };
  }

  formatAttendancePercent(val?: number | null): string {
    if (val === null || val === undefined || isNaN(val)) return '0%';
    return new Intl.NumberFormat('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(val) + '%';
  }

  getMatchUpdateTime(): string {
    const up = this.matchLive()?.updatedAt;
    const d = up ? new Date(up) : new Date();

    const date = isNaN(d.getTime()) ? new Date() : d;

    return `${String(date.getUTCDate()).padStart(2, '0')}/${String(date.getUTCMonth() + 1).padStart(2, '0')} ${date.toISOString().slice(11, 19)}`;
  }

  getStaffRatingsList(ratings?: Record<string, number | undefined>): { label: string; value: number }[] {
    if (!ratings) return [];
    const map: Record<string, string> = {
      GOALKEEPER: 'POR',
      DEFENCE: 'DEF',
      MIDFIELD: 'MED',
      FORWARD: 'DEL',
    };
    return Object.entries(ratings).map(([key, val]) => ({
      label: map[key] || key,
      value: val ?? 1.0,
    }));
  }

  isRelegationRow(index: number): boolean {
    const total = this.standings().length;
    if (total <= 8) {
      return total >= 4 && index >= total - 2;
    }
    return index >= total - 3;
  }

  isFirstRelegationRow(index: number): boolean {
    const total = this.standings().length;
    if (total <= 8) {
      return total >= 4 && index === total - 2;
    }
    return index === total - 3;
  }

  getRowBackgroundClass(row: StandingsRow, index: number): string {
    if (this.isUserClub(row)) {
      return 'bg-emerald-50/80 text-emerald-900 font-bold dark:bg-emerald-950/40 dark:text-emerald-200';
    }
    if (this.isRelegationRow(index)) {
      return 'bg-rose-50/70 hover:bg-rose-100/60 text-slate-800 dark:bg-rose-950/25 dark:hover:bg-rose-950/40 dark:text-slate-200';
    }
    return 'hover:bg-slate-50 text-slate-700 dark:hover:bg-slate-800/40 dark:text-slate-300';
  }

  getTierBorderClass(index: number): string {
    const total = this.standings().length;
    if (total <= 8) {
      if (index === 0) return 'border-l-2 border-l-blue-500';
      if (index === 1) return 'border-l-2 border-l-blue-500 !border-b-2';
      if (this.isFirstRelegationRow(index)) return 'border-l-2 border-l-rose-500 !border-t-2';
      if (this.isRelegationRow(index)) return 'border-l-2 border-l-rose-500';
      return '';
    }
    if (index < 2) return 'border-l-2 border-l-blue-500';
    if (index === 2) return 'border-l-2 border-l-blue-500 !border-b-2';
    //if (index === 4) return 'border-l-2 border-l-amber-500';
    //if (index === 5) return 'border-l-2 border-l-amber-500 !border-b-2 !border-b-amber-500';
    //if (index === 6) return 'border-l-2 border-l-emerald-500 !border-b-2 !border-b-emerald-500';
    if (this.isFirstRelegationRow(index)) return 'border-l-2 border-l-rose-500 !border-t-2';
    if (this.isRelegationRow(index)) return 'border-l-2 border-l-rose-500';
    return '';
  }

  getTierTextClass(index: number): string {
    const total = this.standings().length;
    if (total <= 8) {
      if (index === 0 || index === 1) return 'text-blue-600 dark:text-blue-400 font-bold';
      if (this.isRelegationRow(index)) return 'text-rose-600 dark:text-rose-400 font-bold';
      return 'text-slate-500 dark:text-slate-400';
    }
    if (index < 3) return 'text-blue-600 dark:text-blue-400 font-bold';
    //if (index < 6) return 'text-amber-600 dark:text-amber-400 font-bold';
    //if (index === 6) return 'text-emerald-600 dark:text-emerald-400 font-bold';
    if (this.isRelegationRow(index)) return 'text-rose-600 dark:text-rose-400 font-bold';
    return 'text-slate-500 dark:text-slate-400';
  }

  getClubForRow(row: StandingsRow): any {
    const isUser = this.isUserClub(row);
    if (isUser) {
      return this.game.userClub();
    }
    const found = this.game.clubs().find((c) => c.id === row.clubId);
    if (found) return found;
    return {
      id: row.clubId,
      name: row.clubName,
      code: row.clubCode,
      primaryColor: this.getClubColor(row),
    };
  }

  getClubForFixture(clubId: string, name: string, code: string): any {
    if (clubId === this.game.userClub().id || clubId === this.game.activeUserClubId()) {
      return this.game.userClub();
    }
    const found = this.game.clubs().find((c) => c.id === clubId);
    if (found) return found;
    return {
      id: clubId,
      name,
      code,
      primaryColor: this.getClubBadgeColor(clubId, name),
    };
  }

  onEscape(): void {
    this.closeInspectModal();
  }
}
